import React, { useState, useMemo } from 'react';
import { styled, keyframes } from '@compiled/react';
import { IconButton } from '@atlaskit/button/new';
import ChevronLeftLargeIcon from '@atlaskit/icon/glyph/chevron-left-large';
import ChevronRightLargeIcon from '@atlaskit/icon/glyph/chevron-right-large';
import Image from '@atlaskit/image';
import { Inline, Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	PRODUCT_DISCOVERY_PROJECT,
	type ProjectType,
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
} from '@atlassian/jira-common-constants';
import { useIntl } from '@atlassian/jira-intl';
import {
	UpdateAnimator,
	type AnimationProps,
} from '@atlassian/jira-packages-components-animations/src/update-animator';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller';
import { fireButtonAnalytics } from '../../../common/utils';
import jpdStorageLimit from './assets/jpd-storage-limit.png';
import jpdUserLimit from './assets/jpd-user-limit.png';
import jsmStorageLimit from './assets/jsm-storage-limit.png';
import jsmUserLimit from './assets/jsm-user-limit.png';
import jswStorageLimit from './assets/jsw-storage-limit.png';
import jswUserLimit from './assets/jsw-user-limit.png';
import { BenefitsSlide } from './benefits-slide';
import messages from './messages';

type CarouselProps = {
	projectType: ProjectType;
};
export const Carousel = ({ projectType }: CarouselProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [currentPage, setPage] = useState(0);
	const isSiteAdmin = useIsSiteAdmin();

	const slideContent = useMemo(() => {
		const slides = [];

		// add the specific image by project type
		if (projectType === SERVICE_DESK_PROJECT) {
			slides.push({
				caption: formatMessage(messages.unlimitedAgents),
				component: (
					<Inline xcss={BenefitsImageStyles}>
						<Image src={jsmUserLimit} alt={formatMessage(messages.jsmUserLimitImageAlt)} />
					</Inline>
				),
			});
			slides.push({
				caption: formatMessage(messages.increasedStorageLimits),
				component: (
					<Inline xcss={BenefitsImageStyles}>
						<Image src={jsmStorageLimit} alt={formatMessage(messages.jsmStorageLimitImageAlt)} />
					</Inline>
				),
			});
		} else if (projectType === SOFTWARE_PROJECT) {
			slides.push({
				caption: formatMessage(messages.unlimitedUsers),
				component: (
					<Inline xcss={BenefitsImageStyles}>
						<Image src={jswUserLimit} alt={formatMessage(messages.jswUserLimitImageAlt)} />
					</Inline>
				),
			});
			slides.push({
				caption: formatMessage(messages.increasedStorageLimits),
				component: (
					<Inline xcss={BenefitsImageStyles}>
						<Image src={jswStorageLimit} alt={formatMessage(messages.jswStorageLimitImageAlt)} />
					</Inline>
				),
			});
		} else if (projectType === PRODUCT_DISCOVERY_PROJECT) {
			slides.push({
				caption: formatMessage(messages.unlimitedCreators),
				component: (
					<Inline xcss={BenefitsImageStyles}>
						<Image src={jpdUserLimit} alt={formatMessage(messages.jpdUserLimitImageAlt)} />
					</Inline>
				),
			});
			slides.push({
				caption: formatMessage(messages.increasedStorageLimits),
				component: (
					<Inline xcss={BenefitsImageStyles}>
						<Image src={jpdStorageLimit} alt={formatMessage(messages.jpdStorageLimitImageAlt)} />
					</Inline>
				),
			});
		}

		if (isSiteAdmin) {
			slides.push({
				caption: formatMessage(messages.standardPlanFeatures),
				component: <BenefitsSlide projectType={projectType} />,
			});
		}
		return slides;
	}, [formatMessage, isSiteAdmin, projectType]);

	const numberOfPages = slideContent.length;

	const onPrev = () => {
		if (currentPage === 0) {
			setPage(numberOfPages - 1);
		} else {
			setPage(currentPage - 1);
		}
		fireButtonAnalytics(createAnalyticsEvent, 'previousSlide');
	};
	const onNext = () => {
		if (currentPage === numberOfPages - 1) {
			setPage(0);
		} else {
			setPage(currentPage + 1);
		}
		fireButtonAnalytics(createAnalyticsEvent, 'nextSlide');
	};

	return (
		<Box xcss={CarouselWrapperStyles}>
			<UpdateAnimator
				animationOut={FadeOutAnimation}
				animationIn={FadeInAnimation}
				updateProps={{
					slide: slideContent[currentPage].component,
				}}
			>
				{({ slide }) => slide}
			</UpdateAnimator>
			<Box xcss={ControlsWrapperStyles}>
				<Box xcss={SlideCaptionStyles}>
					<UpdateAnimator
						animationOut={FadeOutAnimation}
						animationIn={FadeInAnimation}
						updateProps={{
							caption: slideContent[currentPage].caption,
						}}
					>
						{({ caption }) => caption}
					</UpdateAnimator>
				</Box>
				<Box xcss={CarouselControlsStyles}>
					<IconButton
						appearance="subtle"
						icon={() => (
							<ChevronLeftLargeIcon
								label="To previous"
								primaryColor={token('color.icon.subtle', '#626F86')}
							/>
						)}
						label="To previous"
						onClick={onPrev}
					/>
					{slideContent.map((_, i) => {
						const isSelected = currentPage === i;
						return (
							<Box key={i} xcss={isSelected ? IndicatorDotSelectedStyles : IndicatorDotStyles} />
						);
					})}
					<IconButton
						appearance="subtle"
						icon={() => (
							<ChevronRightLargeIcon
								label="To next"
								primaryColor={token('color.icon.subtle', '#626F86')}
							/>
						)}
						label="To next"
						onClick={onNext}
					/>
				</Box>
			</Box>
		</Box>
	);
};

const CarouselWrapperStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	marginBottom: token('space.300', '24px'),
});

const fadeIn = keyframes({
	'0%': {
		opacity: 0.2,
	},
	'100%': {
		opacity: 0.8,
	},
});

const fadeOut = keyframes({
	'0%': {
		opacity: 0.8,
	},
	'100%': {
		opacity: 0.2,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FadeOutAnimation = styled.div<AnimationProps>({
	animation: `${fadeOut} 125ms ease-in-out`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FadeInAnimation = styled.div<AnimationProps>({
	animation: `${fadeIn} 125ms ease-in-out`,
});

const BenefitsImageStyles = xcss({
	width: '400px',
	height: '250px',
	borderRadius: 'border.radius.100',
	boxShadow: 'elevation.shadow.overlay',
	overflow: 'hidden',
});

const ControlsWrapperStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
});

const SlideCaptionStyles = xcss({
	color: 'color.text.subtle',
	textAlign: 'center',
	paddingTop: token('space.300', '24px'),
	paddingBottom: token('space.200', '16px'),
});

const CarouselControlsStyles = xcss({
	height: '19px',
	display: 'flex',
	padding: 'space.0',
	justifyContent: 'center',
	alignItems: 'center',
	gap: 'space.100',
	alignSelf: 'stretch',
});

const IndicationDotStyle = {
	height: '8px',
	width: '8px',
	borderTopLeftRadius: '100px',
	borderTopRightRadius: '100px',
	borderBottomRightRadius: '100px',
	borderBottomLeftRadius: '100px',
};

const IndicatorDotStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...IndicationDotStyle,
	backgroundColor: 'color.background.accent.blue.subtler',
});

const IndicatorDotSelectedStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...IndicationDotStyle,
	backgroundColor: 'color.background.information.bold',
});
